import axios, {
    AxiosInstance,
    AxiosResponse,
    AxiosError,
    InternalAxiosRequestConfig,
} from 'axios';
import Cookies from 'js-cookie';
import platform from 'platform';
import { APIUrlDefault, setCookiesCsrfToken } from '../utils/configuration';
import { DEVICE_ID_KEY, TOKEN_KEY, REFRESH_TOKEN_KEY, URL_RETURN_KEY, CSRF_TOKEN_KEY } from '../config/common';
// -----------------------------
// Interface Definitions
// -----------------------------
interface InternalsType {
    get<T = unknown>(url: string, params?: any, options?: any): Promise<T>;
    post<T = unknown>(url: string, payload?: any, options?: any): Promise<T>;
    put<T = unknown>(url: string, payload?: any, options?: any): Promise<T>;
    delete<T = unknown>(url: string, payload?: any, options?: any): Promise<T>;
    postParams<T = unknown>(url: string, payload?: Record<string, any>, options?: any): Promise<T>;
    getBinary<T = unknown>(url: string, params?: any, responseType?: 'blob' | 'arraybuffer', options?: any): Promise<T>;
    postBinary<T = unknown>(url: string, data: FormData | Blob | ArrayBuffer, options?: any): Promise<T>;
}

interface FailedRequest {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}

// -----------------------------
// Axios Instance Setup
// -----------------------------
const apiUrl = APIUrlDefault || '';
const instance: AxiosInstance = axios.create({
    baseURL: apiUrl,
    timeout: 60000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        device: 'Desktop',
        browser: platform.name,
    },
});

// -----------------------------
// Token Refresh State
// -----------------------------
let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        error ? reject(error) : resolve(token!);
    });
    failedQueue = [];
};

// -----------------------------
// Request Interceptor
// -----------------------------

instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = Cookies.get(TOKEN_KEY);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        const csrfToken = Cookies.get(CSRF_TOKEN_KEY);
        if (csrfToken) {
            config.headers['X-XSRF-TOKEN'] = csrfToken;
        }

        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

// -----------------------------
// Response Interceptor with Refresh Logic
// -----------------------------
instance.interceptors.response.use(
    (response: AxiosResponse) => {
        // console.log('response', response);
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        console.log('error.response?.status', error);
        if (!error.response) {
            // console.error('No response received:', error.message, error);
            try {
                const csrfRes = await axios.get(`${apiUrl}/security/csrf-token`, { withCredentials: true });
                if (csrfRes?.data?.token) {
                    setCookiesCsrfToken(csrfRes.data.token);
                    originalRequest.headers['X-XSRF-TOKEN'] = csrfRes.data.token;
                    return instance(originalRequest);
                } else {
                    redirectToLogin();
                    return Promise.reject(error);
                }
            } catch (err) {
                redirectToLogin();
                return Promise.reject(err);
            }
        }
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const accessToken = Cookies.get(TOKEN_KEY);
            const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
            const returnUrl = Cookies.get(URL_RETURN_KEY);
            const deviceId = Cookies.get(DEVICE_ID_KEY);

            if (!refreshToken) {
                redirectToLogin();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(instance(originalRequest));
                        },
                        reject,
                    });
                });
            }

            isRefreshing = true;

            try {
                const { data } = await axios.post(`${apiUrl}/Account/RefreshToken`, {
                    returnUrl: returnUrl || '/',
                    accessToken,
                    refreshToken,
                    deviceId,
                });
                console.log('get new token', data);
                Cookies.set(TOKEN_KEY, data.content.accessToken);
                Cookies.set(REFRESH_TOKEN_KEY, data.content.refreshToken);
                Cookies.set(URL_RETURN_KEY, data.content.returnUrl || '/');
                Cookies.set(DEVICE_ID_KEY, String(deviceId));
                processQueue(null, data.content.accessToken);

                originalRequest.headers.Authorization = `Bearer ${data.content.accessToken}`;
                return instance(originalRequest);
            } catch (err) {
                processQueue(err, null);
                redirectToLogin();
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        // Handle CSRF token refresh on 403 error
        if (error.response?.status === 403 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const csrfRes = await axios.get(`${apiUrl}/security/csrf-token`, { withCredentials: true });
                if (csrfRes?.data?.token) {
                    setCookiesCsrfToken(csrfRes.data.token);
                    originalRequest.headers['X-XSRF-TOKEN'] = csrfRes.data.token;
                    return instance(originalRequest);
                } else {
                    redirectToLogin();
                    return Promise.reject(error);
                }
            } catch (err) {
                redirectToLogin();
                return Promise.reject(err);
            }
        }

        return Promise.reject(error);
    }
);

// -----------------------------
// Helpers
// -----------------------------
const redirectToLogin = () => {
    Cookies.remove(TOKEN_KEY);
    Cookies.remove(REFRESH_TOKEN_KEY);
    window.location.href = '/login';
};

const handleRequest = <T = unknown>(config: any): Promise<T> =>
    instance(config)
        .then((res) => res.data)
        .catch((error) => {
            if (error?.response?.data) throw error.response.data;
            throw error;
        });

// -----------------------------
// API Methods
// -----------------------------
const internals: InternalsType = {
    get: (url, params, options) =>
        handleRequest({ method: 'GET', url, params, ...options }),

    post: (url, payload, options) =>
        handleRequest({ method: 'POST', url, data: payload, ...options }),

    put: (url, payload, options) =>
        handleRequest({ method: 'PUT', url, data: payload, ...options }),

    delete: (url, payload, options) =>
        handleRequest({ method: 'DELETE', url, data: payload, ...options }),
    postBinary: (url, data, options) =>
        handleRequest({
            method: 'POST',
            url,
            data,
            headers: {
                'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/octet-stream',
                ...(options?.headers || {}),
            },
            ...options,
        }),
    getBinary: (url, params, responseType = 'blob', options) =>
        handleRequest({
            method: 'GET',
            url,
            params,
            responseType,
            ...options,
        }),
    postParams: (url, payload, options) => {
        const formData = new URLSearchParams();
        Object.entries(payload || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value as string);
            }
        });

        return handleRequest({
            method: 'POST',
            url,
            data: formData,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            ...options,
        });
    },
};

// -----------------------------
export default internals;
export { instance };
